<template>
	<div class>
		<!-- Modal Component -->
		<!-- modal-class="modal-add-pre-record modal-fullscreen1 modal-fullscreen-right modal-platform" -->
		<ModalBox v-model="legacyModalVisible" size="sm" hide-close header-class="!hidden"
			wrapper-classes="lg:h-auto lg:max-h-none" body-classes="p-4">

			<div class="modal-container">
				<h4 class="text-headline hidden md:block text-medium mb-2">New Pre-Recorded Stream</h4>
				<p class="text-sm text-dark-8 mb-2">Schedule your recorded file to broadcast on multiple social platforms and
					destinations at the same time.</p>

				<p class="text-center bg-surface-2 text-white rounded" v-if="isNoScheduledSub">
					Please activate your Pre-recorded subscription first.
				</p>
				<FormInput v-if="!isNoScheduledSub" label="Stream Name" v-model="video.name" size="md"
					placeholder="#stream_name" class="mb-5" />

				<div class="" v-if="isLoaded" no-gutter>
					<p class="text-tiny text-surface-text-1 mb-2">The file below will be automatically added to the playlist.</p>
					<p class="text-xs text-surface-text-1 truncate mt-2 md:mt-0 mb-1">{{ filename }}.mp4</p>
					<p class="text-xs text-surface-7 mt-2 md:mt-0 mb-0 flex items-center gap-1">{{ video.size }}
						<Tooltip wrapperClass="block" :content="exceededFileMessage">
							<icon-base class="text-error" v-if="!videoValidate(video)" :id="`${video.from}-${video.duration}`"
								:width="14" :height="14" width-view-box="14" height-view-box="14" fill="none">
								<icon-exclamation-circle />
							</icon-base>
						</Tooltip>
						•
						{{ duration }}
						<icon-base class="mb-1 text-error" v-if="isScheduledTrialSub && parseInt(video.duration) > 60 * 10"
							:title="'Only file under 10 minutes are allowed.'" :id="`${video.from}-${video.duration}`" :width="14"
							:height="15" width-view-box="14" height-view-box="15" fill="none">
							<icon-exclamation-circle />
						</icon-base>
					</p>
				</div>
			</div>
			<template #footer>
					<div class="flex flex-wrap border-t border-surface-3 mt-2 p-4">
						<div class="text-right w-full">
							<FormButton type="secondary" @click="onDismiss" class="mr-2" size="md">
								Cancel
							</FormButton>
							<FormButton type="main" size="md" @click="onConfirm"
								:disabled="!isLoaded || (!video.name || !videoValidate(video)) || (isScheduledTrialSub && parseInt(video.duration) > 60 * 10)">
								Confirm
							</FormButton>
						</div>
					</div>
				</template>
		</ModalBox>
	</div>
</template>

<script>
import ModalBox from '@/components/modals/ModalBox.vue'
import { legacyModalBridgeMixin } from '@/composables/useLegacyModalBridge'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import _ from 'lodash';
import Spinner from "@/components/ui/Spinner.vue";
import StreamService from '@/services/StreamService';
import MetricsService from '@/services/MetricsService'
import IconBase from '@/components/icon/IconBase.vue';
import IconLivestream from '@/components/icon/IconLivestream.vue';
import IconMultistream from '@/components/icon/IconMultistream.vue';
import IconIpcam from '@/components/icon/IconIpcam.vue';
import IconSchedulestream from '@/components/icon/IconSchedulestream.vue';
import IconVodChannel from '@/components/icon/IconVodChannel.vue';
import IconExclamationCircle from '@/components/icon/IconExclamationCircle.vue';
import UserService from '../../services/UserService';
import FormButton from '@/components/Atoms/FormButton.vue';
import FormInput from '@/components/Atoms/FormInput.vue';
import prettyMilliseconds from "pretty-ms";

export default {
	name: 'AddPreRecordStreamModal',
	mixins: [legacyModalBridgeMixin],
	components: {
		ModalBox,
		Tooltip,
		FormButton,
		FormInput,
		Spinner,
		IconBase,
		IconLivestream,
		IconMultistream,
		IconIpcam,
		IconSchedulestream,
		IconVodChannel,
		IconExclamationCircle
	},
	data() {
		return {
			videoFilename: null,
			scheduledStorageLimit: {},
			activeScheduledSub: {},
			isLoaded: false,
			isNoScheduledSub: false
		};
	},
	props: {
		video: {
			type: Object,
		},
		stream: {
			type: Object,
		}
	},
	computed: {
		duration() {
			return prettyMilliseconds(this.video.duration * 1000, { unitCount: 2 })
		},
		legacyModalId() { return 'modal-add-pre-record' + this.video.name },
		preRecordedName() {
			return this.filename + '.mp4'
		},
		filename() {
			this.videoFilename = !this.videoFilename ? this.video.name : this.videoFilename
			if (this.videoFilename.includes('.mp4')) this.videoFilename = this.videoFilename.replace('.mp4', '')
			return this.videoFilename
		},
		isScheduledTrialSub() {
			// Check Pre-recorded Stream 500MB - 7 Days Trial sub
			const subscriptions = this.$store.state.User.subscriptions;
			return subscriptions.some((sub) => sub.package._id === '5cd5485126fd039846a3255f' || sub.package._id === '6362114709950a6a642540c4')
		},
		exceededFileMessage() {
			let errorMessage = 'Only file under ' + this.maxVideoFileSize + 'GB are allowed.'
			if (this.scheduledStorageLimit?.unitsLeft <= 0 || this.video.sizeRaw > this.scheduledStorageLimit?.unitsLeft) {
				errorMessage = 'Your total storage limit has been exceeded. Please upgrade your subscription or delete some files to continue.'
			}
			return errorMessage
		},
		maxVideoFileSize() {
			let maxSize = null;
			const subscriptions = this.$store.state.User.subscriptions;
			const activeScheduledSubs = _.filter(subscriptions, (sub) => sub.category === 'scheduled' && sub.enabled === true)

			activeScheduledSubs.forEach(sub => {
				try {
					if (sub.definitionOverride && sub.definitionOverride.schedulerMaxVideoSize) {
						sub.package.definition.schedulerMaxVideoSize = parseFloat(sub.definitionOverride.schedulerMaxVideoSize)
					}
					if (maxSize) {
						const tmpMaxSize = parseFloat(sub.package.definition.schedulerMaxVideoSize);
						if (tmpMaxSize > maxSize) {
							maxSize = tmpMaxSize;
							this.activeScheduledSub = sub
						}
					} else {
						maxSize = parseFloat(sub.package.definition.schedulerMaxVideoSize);
						this.activeScheduledSub = sub
					}
				} catch (error) {
					console.error(error);
				}
			})

			if (this.scheduledStorageLimit?.unitsLeft > 0 && this.scheduledStorageLimit.unitsLeft < maxSize) {
				maxSize = parseFloat(this.scheduledStorageLimit.unitsLeft / (1000 ** 3)).toFixed(1)
			}
			if (this.scheduledStorageLimit?.unitsLeft <= 0) {
				maxSize = 0
			}
			return maxSize;
		},
		activeTeam() {
			return this.$store.state.User.activatedTeam;
		}
	},
	async mounted() {
		while (!this.$store.state.User) {
			// Waiting
		}
		const subscriptions = this.$store.state.User.subscriptions;
		const activeScheduledSubs = _.filter(subscriptions, (sub) => sub.category === 'scheduled' && sub.enabled === true)

		let maxSize
		activeScheduledSubs.forEach(sub => {
			try {
				if (sub.definitionOverride && sub.definitionOverride.schedulerMaxVideoSize) {
					sub.package.definition.schedulerMaxVideoSize = parseFloat(sub.definitionOverride.schedulerMaxVideoSize)
				}
				if (maxSize) {
					const tmpMaxSize = parseFloat(sub.package.definition.schedulerMaxVideoSize);
					if (tmpMaxSize > maxSize) {
						maxSize = tmpMaxSize;
						this.activeScheduledSub = sub
					}
				} else {
					maxSize = parseFloat(sub.package.definition.schedulerMaxVideoSize);
					this.activeScheduledSub = sub
				}
			} catch (error) {
				console.error(error);
			}
		})
		if (!this.activeScheduledSub.package) {
			this.isNoScheduledSub = true
			return
		}
		const res = await MetricsService.getSubscriptionStorage(UserService.getUserId(this.activeTeam), this.activeScheduledSub.package._id)
		this.scheduledStorageLimit = res ? res : {}
		this.isLoaded = true
	},
	methods: {
		videoValidate(video) {
			return (video.sizeRaw <= parseFloat(this.maxVideoFileSize) * (1000 ** 3) && video.sizeRaw <= this.scheduledStorageLimit.unitsLeft && this.scheduledStorageLimit.unitsLeft > 0)

		},
		onDismiss() {
			this.$emit('modal-dismiss');
		},
		async onConfirm() {
			try {
				// Use Vuex store for regions (get from getter, don't mutate)
				await this.$store.dispatch('Regions/getRegions', 'scheduled');
				this.regions = _.cloneDeep(this.$store.getters['Regions/getRegionsByCategory']('scheduled'));
				const region = _.head(this.regions.filter(r => r.platforms.indexOf('scheduled') > -1));
				const streamObj = await StreamService.addStream(this.video.name, region._id, 'scheduled');
				console.log('streamObj', JSON.stringify(streamObj));
				// Create playlist data
				const videoUrl = this.video?.downloadUrl
				const fileName = videoUrl ? videoUrl.substring(videoUrl.indexOf(this.video.streamKey)) : this.video.fileName
				const prefix = this.video.abr ? 'edge-en' : 'edge'
				const videoId = `episodes/${prefix}/${this.stream.user}/${fileName}`
				const durationSec = this.video.duration;
				const durationMins = Math.round((durationSec < 60 ? 60 : durationSec) / 60);

				const payload = {
					id: this.video.vodId || videoId,
					fileName: this.preRecordedName,
					type: 'live-to-vod',
					bytes: this.video.sizeRaw || null,
					mediaInfo: {
						codecs: [{ type: 'video', codec: 'AVC' }, { type: "audio", codec: "AAC" }],
						duration: durationSec,
						durationMins
					}
				}
				console.log('scheduledConfig.payload', JSON.stringify(payload));
				const scheduledConfig = await StreamService.saveStreamPlaylistVideo(streamObj._id, payload)
				console.log('scheduledConfig', JSON.stringify(scheduledConfig));
				window.location.href = `/app/scheduled/${streamObj._id}/dashboard`
			} catch (err) {
				console.error(err);
				this.$notify({ group: 'error', text: err.message });
				await this.$store.dispatch('Streams/getStreams');
				// this.$notify({ group: 'error', text: 'You already reached pre-recorded stream limit. Please upgrade to have more available slots.' });
			}
			this.$emit('modal-confirm');
		}
	}
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.stream-item {
	padding: 16px 26px;
	position: relative;
	/* margin-bottom: 20px; */
	cursor: pointer;
}

.stream-item__desc {
	position: relative;
	flex-grow: 1;
}

.stream-item:not(:last-child) .stream-item__desc:after {
	content: '';
	display: block;
	border-top: 1px solid var(--c-dark-3);
	width: 100%;
	height: 1px;
	position: absolute;
	left: 0;
	bottom: -15px;
}

.stream-item:hover {
	background-color: var(--c-dark-3);
}

.stream-item.active {
	background-color: var(--c-main-1);
}

.stream-item.active .stream-item__desc:after {
	content: none;
}

.stream-item svg {
	color: var(--c-main-1);
}

.stream-item:hover svg,
.stream-item.active svg {
	color: #fff;
}

.stream-item.active .stream-item__p {
	color: #fff;
}

.stream-item__title {
	font-size: var(--font-s-m);
}

.stream-item__p {
	font-size: var(--font-s-s);
}

/* .hideform {
		display: none;
	}
	.showform {
		display: flex;
	} */
.second-modal {
	position: fixed;
	top: 0;
	background-color: var(--c-dark-2);
	margin: auto;
	width: 400px;
	height: 100%;
	-webkit-transition: opacity 0.3s linear, right 0.3s ease-out;
	-moz-transition: opacity 0.3s linear, right 0.3s ease-out;
	-o-transition: opacity 0.3s linear, right 0.3s ease-out;
	transition: opacity 0.3s linear, right 0.3s ease-out;
	right: 0;
	z-index: -1;
	border-left: 1px solid var(--c-dark-3);
	border-right: 1px solid var(--c-dark-3);
}

.showform.second-modal {
	right: 400px;
	-webkit-transition: opacity 0.3s linear, right 0.3s ease-out;
	-moz-transition: opacity 0.3s linear, right 0.3s ease-out;
	-o-transition: opacity 0.3s linear, right 0.3s ease-out;
	transition: opacity 0.3s linear, right 0.3s ease-out;
}

.stream-type-info {
	/* background-color: var(--c-dark-3); */
	padding: 0px;
}

.region-dropdown .dropdown-menu {
	left: -100% !important;
}

.region-flag {
	width: 20px;
}

.modal-logo {
	padding-left: 9px;
}

.stream-type-info h4,
.stream-type-info p {
	cursor: pointer;
}

@media (max-width: 767px) {
	.mobile-header {
		border-radius: 24px 24px 0 0;
		margin-top: 0;
		background-color: var(--c-dark-3);
		border-bottom: 1px solid var(--c-dark-5);
	}

	.hideform.second-modal {
		bottom: -115vh;
		left: 0;
		z-index: 0;
		max-width: 100%;
		transition: opacity 0.3s linear, bottom 0.3s ease-out;
	}

	.showform.second-modal {
		height: auto;
		top: auto;
		bottom: 0;
		left: 0;
		z-index: 1030;
		max-width: 100%;
		transition: opacity 0.3s linear, bottom 0.3s ease-out;
	}

	.modal-logo {
		display: none;
	}

	.modal-title {
		padding: 16px 0;
	}

	.modal-title-style2 {
		display: none;
	}

	.second-modal {
		width: 100%;
		border-left: none;
		border-right: none;
	}

	.btn-close {
		background-color: var(--c-dark-5);
		border-radius: 50%;
		color: var(--c-dark-8);
		height: 32px;
		width: 32px;
	}

	.mt-200 {
		margin-top: 0;
	}

	.stream-item {
		padding: 15px;
	}

	.btn-icon {
		color: var(--c-dark-7);
	}

	:deep(.region-dropdown .dropdown-menu) {
		max-width: 315px;
	}
}

@media (min-width: 768px) and (max-width: 991px) {
	.showform.second-modal {
		right: 100%;
	}
}
</style>
